const toString = Object.prototype.toString;

/**
 * @description: Bir değerin belirtilen tipe ait olup olmadığını kontrol eder.
 */
export function is(val: unknown, type: string) {
  return toString.call(val) === `[object ${type}]`;
}

/**
 * @description: Fonksiyon olup olmadığını kontrol eder
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function isFunction<T = Function>(val: unknown): val is T {
  return is(val, 'Function');
}

/**
 * @description: Tanımlı olup olmadığını kontrol eder
 */
export const isDef = <T = unknown>(val?: T): val is T => {
  return typeof val !== 'undefined';
};

export const isUnDef = <T = unknown>(val?: T): val is T => {
  return !isDef(val);
};

/**
 * @description: Nesne olup olmadığını kontrol eder
 */
export const isObject = (val: any): val is Record<any, any> => {
  return val !== null && is(val, 'Object');
};

/**
 * @description: Zaman (Date) olup olmadığını kontrol eder
 */
export function isDate(val: unknown): val is Date {
  return is(val, 'Date');
}

/**
 * @description: Sayı (Number) olup olmadığını kontrol eder
 */
export function isNumber(val: unknown): val is number {
  return is(val, 'Number');
}

/**
 * @description: Asenkron Fonksiyon (AsyncFunction) olup olmadığını kontrol eder
 */
export function isAsyncFunction<T = any>(val: unknown): val is Promise<T> {
  return is(val, 'AsyncFunction');
}

/**
 * @description: Promise olup olmadığını kontrol eder
 */
export function isPromise<T = any>(val: unknown): val is Promise<T> {
  return is(val, 'Promise') && isObject(val) && isFunction(val.then) && isFunction(val.catch);
}

/**
 * @description: String olup olmadığını kontrol eder
 */
export function isString(val: unknown): val is string {
  return is(val, 'String');
}

/**
 * @description: Boolean olup olmadığını kontrol eder
 */
export function isBoolean(val: unknown): val is boolean {
  return is(val, 'Boolean');
}

/**
 * @description: Dizi (Array) olup olmadığını kontrol eder
 */
export function isArray(val: any): val is Array<any> {
  return val && Array.isArray(val);
}

/**
 * @description: İstemci (client) olup olmadığını kontrol eder
 */
export const isClient = () => {
  return typeof window !== 'undefined';
};

/**
 * @description: Tarayıcı penceresi (Window) olup olmadığını kontrol eder
 */
export const isWindow = (val: any): val is Window => {
  return typeof window !== 'undefined' && is(val, 'Window');
};

export const isElement = (val: unknown): val is Element => {
  return isObject(val) && !!val.tagName;
};

export const isServer = typeof window === 'undefined';

// Resim öğesi (image element) olup olmadığını kontrol eder
export function isImageDom(o: Element) {
  return o && ['IMAGE', 'IMG'].includes(o.tagName);
}

export function isNull(val: unknown): val is null {
  return val === null;
}

export function isNullAndUnDef(val: unknown): val is null | undefined {
  return isUnDef(val) && isNull(val);
}

export function isNullOrUnDef(val: unknown): val is null | undefined {
  return isUnDef(val) || isNull(val);
}

/**
 * @description: String'in null, undefined veya boş olup olmadığını kontrol eder
 */
export function isNullOrEmpty(val: unknown): val is string {
  return isNullOrUnDef(val) || (isString(val) && val.trim() === '');
}

/**
 * @description: JSON nesnesi olup olmadığını kontrol eder
 */
export function isJson(val: unknown): val is Record<string, any> {
  if (!isString(val)) {
    return false;
  }

  try {
    const parsedJson = JSON.parse(val);
    return typeof parsedJson === 'object' && parsedJson !== null;
  } catch (error) {
    return false;
  }
}
